import Link from "next/link";
import { FunctionComponent, useEffect, useState } from "react";
import { useUser } from "../../hooks/useUser";
import { memberSignUp, signIn } from "../../lib/auth-fetcher";
import Button from "../lib/Button";
import { useRedirect } from "../../hooks/useRedirect";
import { hostSlug, reservedAppSlugs } from "../../lib/host";
import { Team, TeamMembershipSettings } from "@prisma/client";
import Avatar from "@xtribe/shared/components/lib/Avatar";
import { cache } from "swr";
import Input from "../lib/Input";
import { useForm } from "react-hook-form";
import CheckBox from "../lib/CheckBox/CheckBox";
import Image from "next/image";

type SignInFormProps = {
  team?: Team;
  queryString: string | null;
};

type IFormData = {
  email: string;
  password: string;
};

const SignInForm: FunctionComponent<SignInFormProps> = ({
  team,
  queryString,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState<string>("");
  const { user, loading, loggedOut, error, mutate } = useUser();
  const { pushToNextPath, getNextPath } = useRedirect({
    defaultPath: "/member",
  });
  const { register, handleSubmit, errors } = useForm<IFormData>();

  useEffect(() => {
    if (user?.id && !isLoading) {
      const slug = hostSlug(window);
      cache.clear();
      let nextPath =
        slug && !reservedAppSlugs.includes(slug)
          ? getNextPath()
            ? getNextPath()
            : "/member"
          : process.env.NEXT_PUBLIC_APP_URL + "/user";
      pushToNextPath(nextPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isLoading]);

  async function onSubmit({ email, password }: IFormData) {
    if (isLoading || loading) return;
    setErrorMsg("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      let res: { user: any } = { user: null };
      res = await signIn({
        email: email?.trim(),
        password: password?.trim(),
      });
      if (res) {
        await mutate({ user: res.user });
      }
      setIsLoading(false);
    } catch (error) {
      setErrorMsg(error.message);
      setSuccessMsg("");
      setIsLoading(false);
    }
  }

  let authText = "Anmelden";

  const passwordHref = `${process.env.NEXT_PUBLIC_AUTH_URL}/forgot-password${
    queryString ? "?" + queryString : ""
  }`;
  return (
    <div className="max-w-md w-full mx-auto">
      <div className="mt-4">
        <div className="flex flex-col justify-center items-center text-center">
          {team ? (
            <Avatar
              storageLink={!!team}
              src={team?.avatar ?? "no-image.svg"}
              size="large"
            />
          ) : (
            <Image
              src="/static/logo/xtribe-logo-128.png"
              height={60}
              width={205.78125}
            />
          )}
          <h1 className="normal-case tracking-tight mb-8 mt-4 h4">
            {team ? (
              <>
                Anmeldung <span className="font-semibold">{team.name}</span>
              </>
            ) : (
              "Bei xTribe Anmelden"
            )}
          </h1>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4 sm:mb-6">
            <Input
              name="email"
              type="email"
              autoComplete="email"
              width="100%"
              size="large"
              placeholder="hello@example.com"
              ref={register({
                required: true,
                pattern: {
                  message: "Keine gültige Email",
                  value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                },
              })}
            >
              Email Adresse
            </Input>
            {errors.email && (
              <p className="text-sm mt-2 text-red-500">
                {errors.email.message
                  ? errors.email.message
                  : "Dieses Feld wird benötigt"}
              </p>
            )}
          </div>
          <div className="mb-4 sm:mb-6">
            <Input
              name="password"
              type="password"
              autoComplete="password"
              placeholder="passwort123"
              width="100%"
              size="large"
              ref={register({
                required: true,
                minLength: {
                  value: 8,
                  message: "Das Passwort muss mindestens 8 Zeichen lang sein.",
                },
              })}
            >
              Passwort
            </Input>
            {errors.password && (
              <p className="text-sm mt-2 text-red-500">
                {errors.password.message
                  ? errors.password.message
                  : "Dieses Feld wird benötigt"}
              </p>
            )}
          </div>
          {errorMsg && <p className="text-red-500 text-sm mb-4">{errorMsg}</p>}
          {!!successMsg && (
            <p className="text-green-500 text-sm mb-4">{successMsg}</p>
          )}
          <div className="flex flex-col ">
            <div className="flex flex-col w-full">
              <Button
                type="success"
                htmlType="submit"
                loading={isLoading || loading}
                className="mb-8"
              >
                {authText}
              </Button>
            </div>
            <p className="text-sm mb-4">
              Noch kein Account?&nbsp;
              <Link href={`/signup${queryString ? "?" + queryString : ""}`}>
                <a className="text-primary font-semibold no-underline hover:underline">
                  Jetzt registrieren
                </a>
              </Link>
            </p>
            <a
              href={passwordHref}
              className="text-sm text-gray-500 font-medium self-start hover:text-primary-dark"
            >
              Passwort vergessen?
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignInForm;
